import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { Plus, PencilSimple, Trash, Sparkle, CaretDown, CaretRight, X, StackPlus, StackMinus, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import AIGenerateModal from "../../components/AIGenerateModal";
import ImportQuestionsButton from "../../components/ImportQuestionsButton";
import RichText from "../../components/RichText";
import { MediaRenderer } from "../../components/MediaAttachments";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

const UNASSIGNED_KEY = "__unassigned__";

export default function QuestionBank() {
  const [items, setItems] = useState([]);
  const [exams, setExams] = useState([]);
  const [filter, setFilter] = useState({ exam_id: "", difficulty: "", search: "" });
  const [loading, setLoading] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({}); // { exam_id: true }
  const [selected, setSelected] = useState(new Set()); // question_ids
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState("add"); // add | remove | move
  const [bulkTarget, setBulkTarget] = useState("");
  const [bulkSource, setBulkSource] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (filter.difficulty) params.difficulty = filter.difficulty;
    if (filter.search) params.search = filter.search;
    api.get("/questions", { params }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/exams").then((r) => setExams(r.data || []));
    load();
  }, []);

  // Reload questions on search/difficulty change (exam filter is applied client-side).
  useEffect(load, [filter.difficulty, filter.search]);

  const remove = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    await api.delete(`/questions/${id}`);
    toast.success("Deleted");
    load();
  };

  // Build a map: question_id -> [exam_id, ...] so we can group efficiently.
  const questionToExams = useMemo(() => {
    const m = {};
    exams.forEach((e) => {
      (e.question_ids || []).forEach((qid) => {
        if (!m[qid]) m[qid] = [];
        m[qid].push(e.exam_id);
      });
    });
    return m;
  }, [exams]);

  // Group questions by exam (each question can appear under multiple exams).
  // When a specific exam is selected, show a flat list of only that exam's questions.
  const grouped = useMemo(() => {
    if (filter.exam_id) {
      const eid = filter.exam_id;
      const list = items.filter((q) => (questionToExams[q.question_id] || []).includes(eid));
      if (list.length === 0) return [];
      const exam = exams.find((e) => e.exam_id === eid);
      return [{ key: eid, exam, questions: list }];
    }
    const buckets = new Map(); // exam_id -> {exam, questions}
    exams.forEach((e) => buckets.set(e.exam_id, { key: e.exam_id, exam: e, questions: [] }));
    const unassigned = { key: UNASSIGNED_KEY, exam: null, questions: [] };
    items.forEach((q) => {
      const eids = questionToExams[q.question_id] || [];
      if (eids.length === 0) {
        unassigned.questions.push(q);
      } else {
        eids.forEach((eid) => {
          const b = buckets.get(eid);
          if (b) b.questions.push(q);
        });
      }
    });
    const arr = Array.from(buckets.values()).filter((b) => b.questions.length > 0);
    if (unassigned.questions.length > 0) arr.push(unassigned);
    return arr;
  }, [items, exams, filter.exam_id, questionToExams]);

  const totalQuestions = items.length;
  const toggle = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  const toggleSelect = (qid) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(qid)) n.delete(qid); else n.add(qid);
      return n;
    });

  const selectAllInGroup = (groupQuestions, allSelected) =>
    setSelected((s) => {
      const n = new Set(s);
      groupQuestions.forEach((q) => {
        if (allSelected) n.delete(q.question_id); else n.add(q.question_id);
      });
      return n;
    });

  const clearSelection = () => setSelected(new Set());

  const openBulk = (mode) => {
    setBulkMode(mode);
    setBulkTarget("");
    // If a specific exam is filtered, pre-fill it as the source for remove/move
    setBulkSource(filter.exam_id || "");
    setBulkOpen(true);
  };

  const refreshExams = async () => {
    const er = await api.get("/exams");
    setExams(er.data || []);
  };

  const submitBulk = async () => {
    const qids = Array.from(selected);
    if (qids.length === 0) { toast.error("No questions selected"); return; }
    setBulkBusy(true);
    try {
      if (bulkMode === "add") {
        if (!bulkTarget) { toast.error("Pick a target exam"); return; }
        const r = await api.post(`/exams/${bulkTarget}/questions/add`, { question_ids: qids });
        const added = r.data?.added ?? 0;
        const dropped = (r.data?.dropped_ids || []).length;
        const skipped = qids.length - added - dropped;
        const t = exams.find((e) => e.exam_id === bulkTarget)?.title || bulkTarget;
        toast.success(`Added ${added} to "${t}"${skipped ? ` · ${skipped} already present` : ""}${dropped ? ` · ${dropped} dropped` : ""}`);
      } else if (bulkMode === "remove") {
        if (!bulkSource) { toast.error("Pick the exam to remove from"); return; }
        const r = await api.post(`/exams/${bulkSource}/questions/remove`, { question_ids: qids });
        const removed = r.data?.removed ?? 0;
        const src = exams.find((e) => e.exam_id === bulkSource)?.title || bulkSource;
        toast.success(`Removed ${removed} from "${src}"`);
      } else if (bulkMode === "move") {
        if (!bulkTarget) { toast.error("Pick a target exam"); return; }
        const body = { question_ids: qids, target_exam_id: bulkTarget };
        if (bulkSource) body.source_exam_ids = [bulkSource];
        const r = await api.post(`/exams/questions/move`, body);
        const added = r.data?.added ?? 0;
        const removedList = r.data?.removed_from || [];
        const removedTotal = removedList.reduce((n, x) => n + (x.removed || 0), 0);
        const t = exams.find((e) => e.exam_id === bulkTarget)?.title || bulkTarget;
        toast.success(`Moved ${added} to "${t}" · removed ${removedTotal} from ${removedList.length} exam${removedList.length === 1 ? "" : "s"}`);
      }
      await refreshExams();
      clearSelection();
      setBulkOpen(false);
      setBulkTarget("");
      setBulkSource("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        overline="Question Bank"
        title="Manage questions"
        subtitle="Grouped by exam. Search, filter, AI-generate, import, and edit."
        action={
          <div className="flex flex-wrap gap-2">
            <ImportQuestionsButton onImported={load} />
            <button
              onClick={() => setAiOpen(true)}
              className="border border-zinc-900 px-4 py-2.5 text-sm font-medium hover:bg-zinc-900 hover:text-white inline-flex items-center gap-2 transition-colors"
              data-testid="ai-generate-open-btn"
            >
              <Sparkle size={14} weight="fill" /> AI Generate
            </button>
            <Link to="/app/questions/new" className="bg-zinc-900 text-white px-4 py-2.5 text-sm font-medium hover:bg-zinc-800 inline-flex items-center gap-2" data-testid="add-question-btn">
              <Plus size={14} /> Add question
            </Link>
          </div>
        }
      />
      <AIGenerateModal open={aiOpen} onOpenChange={setAiOpen} onCreated={load} />

      <div className="grid md:grid-cols-3 gap-3 mb-6">
        <input
          placeholder="Search questions…"
          value={filter.search}
          onChange={(e) => setFilter({ ...filter, search: e.target.value })}
          className="border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-900 focus:outline-none"
          data-testid="search-questions-input"
        />
        <select
          value={filter.exam_id}
          onChange={(e) => setFilter({ ...filter, exam_id: e.target.value })}
          className="border border-zinc-300 px-3 py-2.5 text-sm bg-white focus:border-zinc-900 focus:outline-none"
          data-testid="filter-exam"
        >
          <option value="">All exams (grouped)</option>
          {exams.map((e) => (
            <option key={e.exam_id} value={e.exam_id}>{e.title}</option>
          ))}
        </select>
        <select
          value={filter.difficulty}
          onChange={(e) => setFilter({ ...filter, difficulty: e.target.value })}
          className="border border-zinc-300 px-3 py-2.5 text-sm bg-white focus:border-zinc-900 focus:outline-none"
          data-testid="filter-difficulty"
        >
          <option value="">All difficulty</option>
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : totalQuestions === 0 ? (
        <div className="border border-zinc-200 p-10 text-center text-sm text-zinc-500">No questions found.</div>
      ) : grouped.length === 0 ? (
        <div className="border border-zinc-200 p-10 text-center text-sm text-zinc-500" data-testid="no-questions-for-exam">
          No matching questions in this exam.
        </div>
      ) : (
        <div className="space-y-4" data-testid="questions-list">
          {grouped.map(({ key, exam, questions }) => {
            const isCollapsed = !!collapsed[key];
            const isUnassigned = key === UNASSIGNED_KEY;
            const groupIds = questions.map((q) => q.question_id);
            const groupAllSelected = questions.length > 0 && groupIds.every((id) => selected.has(id));
            const groupSomeSelected = groupIds.some((id) => selected.has(id));
            return (
              <div key={key} className="border border-zinc-200" data-testid={`exam-group-${isUnassigned ? "unassigned" : key}`}>
                <div className="flex items-center gap-2 px-3 py-3 bg-zinc-50 border-b border-zinc-200">
                  <input
                    type="checkbox"
                    checked={groupAllSelected}
                    ref={(el) => { if (el) el.indeterminate = !groupAllSelected && groupSomeSelected; }}
                    onChange={() => selectAllInGroup(questions, groupAllSelected)}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 accent-zinc-900 cursor-pointer"
                    aria-label="Select all in group"
                    data-testid={`exam-group-select-all-${isUnassigned ? "unassigned" : key}`}
                  />
                  <button
                    type="button"
                    onClick={() => toggle(key)}
                    className="flex-1 flex items-center justify-between gap-3 hover:bg-zinc-100 -m-1 p-1 text-left"
                    data-testid={`exam-group-toggle-${isUnassigned ? "unassigned" : key}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isCollapsed ? <CaretRight size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                          {isUnassigned ? "Unassigned" : "Exam"}
                        </div>
                        <div className="text-sm font-semibold truncate">
                          {isUnassigned ? "Not used in any exam" : exam?.title || key}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold border border-zinc-300 px-2 py-0.5">
                        {questions.length} question{questions.length === 1 ? "" : "s"}
                      </span>
                    </div>
                  </button>
                </div>
                {!isCollapsed && (
                  <div>
                    {questions.map((q, i) => {
                      const isSelected = selected.has(q.question_id);
                      return (
                      <div key={q.question_id} className={`p-5 ${i < questions.length - 1 ? "border-b border-zinc-200" : ""} ${isSelected ? "bg-blue-50/50" : ""}`} data-testid={`question-row-${q.question_id}`}>
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(q.question_id)}
                            className="mt-1 h-4 w-4 accent-zinc-900 cursor-pointer flex-shrink-0"
                            aria-label="Select question"
                            data-testid={`select-q-${q.question_id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                              <span className="border border-zinc-300 px-2 py-0.5">{qTypeLabel(q.type)}</span>
                              <span className="border border-zinc-300 px-2 py-0.5">{q.difficulty}</span>
                              <span className="border border-zinc-300 px-2 py-0.5">{q.marks} marks</span>
                              {q.topic && <span className="text-zinc-400">· {q.topic}</span>}
                            </div>
                            <div className="text-sm font-medium leading-snug"><RichText html={q.text} /></div>
                            {q.attachments && q.attachments.length > 0 && (
                              <div className="mt-2"><MediaRenderer attachments={q.attachments.slice(0, 3)} inline /></div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Link to={`/app/questions/${q.question_id}`} className="p-2 hover:bg-zinc-100" data-testid={`edit-q-${q.question_id}`}>
                              <PencilSimple size={16} />
                            </Link>
                            <button onClick={() => remove(q.question_id)} className="p-2 hover:bg-red-50 text-red-600" data-testid={`delete-q-${q.question_id}`}>
                              <Trash size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Floating bulk-action bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-zinc-900 text-white shadow-2xl flex flex-wrap items-center gap-2 px-4 py-3 max-w-[95vw]" data-testid="bulk-action-bar">
          <span className="text-sm font-mono-tab font-bold" data-testid="bulk-selection-count">{selected.size}</span>
          <span className="text-xs text-zinc-300 uppercase tracking-wider">selected</span>
          <div className="w-px h-6 bg-zinc-700 hidden sm:block" />
          <button
            onClick={() => openBulk("add")}
            className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 text-xs sm:text-sm font-semibold inline-flex items-center gap-2"
            data-testid="bulk-add-to-exam-btn"
          >
            <StackPlus size={14} weight="bold" /> Add to exam…
          </button>
          <button
            onClick={() => openBulk("remove")}
            className="bg-red-600 hover:bg-red-500 text-white px-3 py-2 text-xs sm:text-sm font-semibold inline-flex items-center gap-2"
            data-testid="bulk-remove-from-exam-btn"
          >
            <StackMinus size={14} weight="bold" /> Remove from exam…
          </button>
          <button
            onClick={() => openBulk("move")}
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 px-3 py-2 text-xs sm:text-sm font-semibold inline-flex items-center gap-2"
            data-testid="bulk-move-to-exam-btn"
          >
            <ArrowsClockwise size={14} weight="bold" /> Move to exam…
          </button>
          <button
            onClick={clearSelection}
            className="p-2 hover:bg-zinc-800 text-zinc-300 hover:text-white"
            aria-label="Clear selection"
            data-testid="bulk-clear-btn"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      )}

      {/* Bulk modal (Add / Remove / Move) */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-add-modal">
          <DialogHeader>
            <DialogTitle>
              {bulkMode === "add" && `Add ${selected.size} question${selected.size === 1 ? "" : "s"} to an exam`}
              {bulkMode === "remove" && `Remove ${selected.size} question${selected.size === 1 ? "" : "s"} from an exam`}
              {bulkMode === "move" && `Move ${selected.size} question${selected.size === 1 ? "" : "s"} to another exam`}
            </DialogTitle>
            <DialogDescription>
              {bulkMode === "add" && "Pick a target exam. Duplicates are skipped automatically and the exam's total marks recalculate."}
              {bulkMode === "remove" && "Pick the exam to remove these questions from. The questions stay in the bank; only their assignment to this exam is removed."}
              {bulkMode === "move" && "Pick a target exam. Questions will be removed from the source exam (or from every exam if left blank) and added to the target."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            {(bulkMode === "remove" || bulkMode === "move") && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold block mb-1.5">
                  {bulkMode === "remove" ? "Source exam" : "Source exam (optional — leave blank to remove from all)"}
                </label>
                <select
                  value={bulkSource}
                  onChange={(e) => setBulkSource(e.target.value)}
                  className="w-full border border-zinc-300 px-3 py-2.5 text-sm bg-white focus:border-zinc-900 focus:outline-none"
                  data-testid="bulk-source-select"
                >
                  <option value="">{bulkMode === "remove" ? "Select an exam…" : "All exams containing the selected questions"}</option>
                  {exams.map((e) => (
                    <option key={e.exam_id} value={e.exam_id}>
                      {e.title} · {(e.question_ids || []).length} q
                    </option>
                  ))}
                </select>
              </div>
            )}
            {(bulkMode === "add" || bulkMode === "move") && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold block mb-1.5">Target exam</label>
                <select
                  value={bulkTarget}
                  onChange={(e) => setBulkTarget(e.target.value)}
                  className="w-full border border-zinc-300 px-3 py-2.5 text-sm bg-white focus:border-zinc-900 focus:outline-none"
                  data-testid="bulk-target-select"
                >
                  <option value="">Select an exam…</option>
                  {exams.map((e) => (
                    <option key={e.exam_id} value={e.exam_id}>
                      {e.title} · {(e.question_ids || []).length} q
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={() => setBulkOpen(false)}
              className="border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-50"
              data-testid="bulk-cancel-btn"
            >Cancel</button>
            <button
              onClick={submitBulk}
              disabled={bulkBusy || (bulkMode === "add" && !bulkTarget) || (bulkMode === "remove" && !bulkSource) || (bulkMode === "move" && !bulkTarget)}
              className={`text-white px-4 py-2 text-sm disabled:opacity-50 inline-flex items-center gap-2 ${
                bulkMode === "remove" ? "bg-red-600 hover:bg-red-500" : bulkMode === "move" ? "bg-amber-500 text-zinc-900 hover:bg-amber-400" : "bg-zinc-900 hover:bg-zinc-800"
              }`}
              data-testid="bulk-confirm-btn"
            >
              {bulkBusy ? "Working…" : (
                <>
                  {bulkMode === "add" && <><StackPlus size={14} weight="bold" /> Add to exam</>}
                  {bulkMode === "remove" && <><StackMinus size={14} weight="bold" /> Remove</>}
                  {bulkMode === "move" && <><ArrowsClockwise size={14} weight="bold" /> Move</>}
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function qTypeLabel(t) {
  return ({ mcq: "MCQ", msq: "Multi", true_false: "T/F", short_answer: "Short" })[t] || t;
}
